/**
 * Entering a receipt by hand: the fallback when extraction fails, and the only path for a cash
 * purchase with no receipt at all (PRODUCT.md §11).
 *
 * Only the total is required. A farmers' market stall gives no receipt, and asking for line items
 * the user does not have would push them to make some up. Lines are optional extras for when
 * splitting a purchase between buckets will matter later.
 */

import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Field, FieldError, Input, Notice } from "@/components/ui/Field";
import { meQuery } from "@/features/auth/session";
import { todayIso } from "@/lib/dates";
import { fieldMessage } from "@/lib/fieldMessage";
import { useCreateManualReceipt } from "./queries";
import {
  emptyManualReceipt,
  manualReceiptSchema,
  toManualReceiptBody,
  type ManualReceiptValues,
} from "./schemas";

export function ManualReceiptPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateManualReceipt();
  // Served from the cache: the route guard has already loaded it.
  const me = useQuery(meQuery);
  const currency = me.data?.workspace?.default_currency ?? "CAD";

  const form = useForm<ManualReceiptValues>({
    resolver: zodResolver(manualReceiptSchema),
    // Most manual receipts are entered the day of the purchase.
    defaultValues: { ...emptyManualReceipt, purchase_date: todayIso() },
  });
  const lines = useFieldArray({ control: form.control, name: "lines" });
  const errors = form.formState.errors;

  const submit = form.handleSubmit(async (values) => {
    try {
      const receipt = await create.mutateAsync(toManualReceiptBody(values, currency));
      await navigate({ to: "/receipts/$receiptId", params: { receiptId: receipt.id } });
    } catch (error) {
      form.setError("root", {
        type: "server",
        message: error instanceof ApiError ? error.message : t("receipts.errors.saveFailed"),
      });
    }
  });

  /** A line's own field error, which `fieldMessage` cannot reach because it is nested. */
  function lineError(index: number, field: "description" | "amount"): string | undefined {
    const message = errors.lines?.[index]?.[field]?.message;
    return message ? t(message) : undefined;
  }

  return (
    <section className="space-y-5">
      <Link
        to="/receipts/new"
        className="-ml-1 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        {t("receipts.capture.title")}
      </Link>
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-stone-900">{t("receipts.manual.title")}</h1>
        <p className="text-sm text-stone-600">{t("receipts.manual.subtitle")}</p>
      </header>

      <form onSubmit={submit} noValidate className="space-y-4">
        <Field
          name="store_name"
          label={t("receipts.manual.store")}
          error={fieldMessage(errors, "store_name", t)}
        >
          <Input
            id="store_name"
            placeholder={t("receipts.manual.storePlaceholder")}
            {...form.register("store_name")}
          />
        </Field>

        <Field name="purchase_date" label={t("receipts.manual.date")}>
          <Input id="purchase_date" type="date" {...form.register("purchase_date")} />
        </Field>

        <Field
          name="total"
          label={t("receipts.manual.total", { currency })}
          error={fieldMessage(errors, "total", t)}
        >
          <Input
            id="total"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0.00"
            invalid={!!errors.total}
            {...form.register("total")}
          />
        </Field>

        <div className="grid grid-cols-[6rem_1fr] gap-3">
          <Field name="tax_label" label={t("receipts.manual.taxLabel")}>
            <Input id="tax_label" {...form.register("tax_label")} />
          </Field>
          <Field
            name="tax"
            label={t("receipts.manual.taxAmount")}
            error={fieldMessage(errors, "tax", t)}
          >
            <Input
              id="tax"
              inputMode="decimal"
              autoComplete="off"
              placeholder={t("receipts.manual.optional")}
              invalid={!!errors.tax}
              {...form.register("tax")}
            />
          </Field>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-stone-700">
            {t("receipts.manual.lines")}
          </legend>
          {lines.fields.map((line, index) => (
            <div key={line.id} className="space-y-1">
              <div className="flex items-start gap-2">
                <Input
                  aria-label={t("receipts.manual.lineDescription")}
                  placeholder={t("receipts.manual.lineDescription")}
                  invalid={!!errors.lines?.[index]?.description}
                  className="flex-1"
                  {...form.register(`lines.${index}.description`)}
                />
                <Input
                  aria-label={t("receipts.manual.lineAmount")}
                  placeholder="0.00"
                  inputMode="decimal"
                  autoComplete="off"
                  invalid={!!errors.lines?.[index]?.amount}
                  className="w-28"
                  {...form.register(`lines.${index}.amount`)}
                />
                <button
                  type="button"
                  aria-label={t("receipts.manual.removeLine")}
                  onClick={() => lines.remove(index)}
                  className="min-h-11 rounded-md px-2 text-stone-500 hover:bg-stone-100"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>
              <FieldError>
                {lineError(index, "description") ?? lineError(index, "amount")}
              </FieldError>
            </div>
          ))}
          <button
            type="button"
            onClick={() => lines.append({ description: "", amount: "" })}
            className="text-brand-700 hover:bg-brand-50 inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-sm font-medium"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t("receipts.manual.addLine")}
          </button>
        </fieldset>

        <Field name="notes" label={t("receipts.manual.notes")}>
          <textarea
            id="notes"
            rows={3}
            className="focus:ring-brand-600 focus:border-brand-600 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-base focus:ring-1 focus:outline-none"
            {...form.register("notes")}
          />
        </Field>

        {errors.root?.message && <Notice tone="error">{errors.root.message}</Notice>}

        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? t("common.saving") : t("receipts.manual.save")}
        </Button>
      </form>
    </section>
  );
}
